import { useRef, useState } from 'react';
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonList,
  IonItem,
  IonInput,
  IonToggle,
  IonButton,
  IonText,
  IonIcon,
  useIonRouter,
  IonSpinner,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { useMutation, useQuery } from '@apollo/client/react';
import { alertCircleOutline, informationCircleOutline, trophyOutline } from 'ionicons/icons';
import { CREATE_FANTASY_LEAGUE } from '../../../graphql/mutations/createFantasyLeague';
import { GET_MY_FANTASY_LEAGUES } from '../../../graphql/queries/myFantasyLeagues';
import { GET_AVAILABLE_COMPETITIONS } from '../../../graphql/queries/getAvailableCompetitions';
import { MAX_PLAYERS_MARKET_MAX, MAX_PLAYERS_MARKET_MIN } from '@/appConfig';
import type {
  CreateFantasyLeagueMutation,
  CreateFantasyLeagueMutationVariables,
  GetAvailableCompetitionsQuery,
} from '../../../types/graphql-generated';
import { CREATE_LEAGUE_INTRO, LEAGUE_INFO_CONTENT } from '@/constants/leagueInfoContent';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { SelectField } from '../../../components/ui/SelectField';
import { CompetitionName } from '../components/CompetitionName';
import { formatCompetitionLabel } from '../components/competitionLabel';
import { compareCompetitionsByRankAndName } from '../components/competitionSorting';
import { OfficialChannelsFollowBlock } from '../components/OfficialChannelsFollowBlock';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FeatureFlag } from '../../../types/featureFlags';

const NAME_REQUIRED_ERROR = 'El nombre de la liga es obligatorio';

const renderCreateFantasyLeaguePageState = (content: React.ReactNode) => (
  <IonPage>
    <AppSectionHeader variant="main">
      <IonToolbar>
        <IonButtons slot="start">
          <IonBackButton defaultHref="/app/main/mis-ligas" />
        </IonButtons>
        <IonTitle className="ml-4">Crear Liga Fantasy</IonTitle>
      </IonToolbar>
    </AppSectionHeader>

    <IonContent className="ion-padding">{content}</IonContent>
  </IonPage>
);

export const CreateFantasyLeague = () => {
  const router = useIonRouter();
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [maxPlayers, setMaxPlayers] = useState(10);
  const [errors, setErrors] = useState<string[]>([]);
  const [showNameError, setShowNameError] = useState(false);
  const [selectedCompetitionCode, setSelectedCompetitionCode] = useState<string | undefined>(undefined);
  const [officialChannelSelections, setOfficialChannelSelections] = useState<Record<string, boolean>>({});
  const competitionSelectRef = useRef<HTMLIonSelectElement>(null);
  const { isEnabled } = useFeatureFlags();

  const {
    data: competitionsData,
    loading: competitionsLoading,
    error: competitionsError,
    refetch: refetchCompetitions,
  } = useQuery<GetAvailableCompetitionsQuery>(GET_AVAILABLE_COMPETITIONS);
  const availableCompetitions = [...(competitionsData?.getAvailableCompetitions ?? [])].sort(
    compareCompetitionsByRankAndName
  );
  // With a single available competition the selector is hidden and the
  // backend falls back to the default competition
  const showCompetitionSelector = availableCompetitions.length > 1;
  const competitionCode = showCompetitionSelector
    ? (selectedCompetitionCode ?? availableCompetitions[0]?.code)
    : undefined;
  const selectedCompetition = availableCompetitions.find((competition) => competition.code === competitionCode);
  // Without the selector the league is created in the only available competition
  const leagueCompetition = showCompetitionSelector ? selectedCompetition : availableCompetitions[0];
  const officialChannels = leagueCompetition?.officialChannels ?? [];
  const showOfficialChannels = isEnabled(FeatureFlag.POSTS) && officialChannels.length > 0;
  const selectedOfficialChannelIds = officialChannels
    .filter((channel) => officialChannelSelections[channel.id] ?? true)
    .map((channel) => channel.id);

  const handleOfficialChannelSelectionChange = (channelIds: string[]) => {
    const selectedIds = new Set(channelIds);
    setOfficialChannelSelections((selections) => ({
      ...selections,
      ...Object.fromEntries(officialChannels.map((channel) => [channel.id, selectedIds.has(channel.id)])),
    }));
  };

  const [createLeague, { loading }] = useMutation<
    CreateFantasyLeagueMutation,
    CreateFantasyLeagueMutationVariables
  >(CREATE_FANTASY_LEAGUE, {
    refetchQueries: [
      {
        query: GET_MY_FANTASY_LEAGUES,
      },
    ],
  });

  if (competitionsLoading) {
    return renderCreateFantasyLeaguePageState(
      <div className="flex min-h-48 items-center justify-center gap-3 text-sm text-slate-600" role="status">
        <IonSpinner name="crescent" />
        <span>Cargando competiciones…</span>
      </div>
    );
  }

  if (competitionsError || !competitionsData) {
    return renderCreateFantasyLeaguePageState(
      <div
        className="mx-auto mt-4 flex w-full max-w-xl flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-center"
        role="alert"
      >
        <IonIcon icon={alertCircleOutline} className="text-2xl text-red-600" aria-hidden="true" />
        <p className="text-sm text-slate-700">
          No se pudieron cargar las competiciones. Comprueba tu conexión e inténtalo de nuevo.
        </p>
        <IonButton
          type="button"
          fill="outline"
          onClick={() => void refetchCompetitions().catch(() => undefined)}
        >
          Reintentar
        </IonButton>
      </div>
    );
  }

  const handleNameInput = (value: string | number | null | undefined) => {
    const nextName = value?.toString() ?? '';
    setName(nextName);
    if (nextName.trim()) setShowNameError(false);
    if (errors.length) setErrors([]);
  };

  const handleMaxPlayersInput = (value: string | number | null | undefined) => {
    if (value === '' || value === null || value === undefined) return;

    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(n)) return;

    let bounded = n;
    if (bounded < MAX_PLAYERS_MARKET_MIN) bounded = MAX_PLAYERS_MARKET_MIN;
    if (bounded > MAX_PLAYERS_MARKET_MAX) bounded = MAX_PLAYERS_MARKET_MAX;

    setMaxPlayers(bounded);
  };

  const isNameInvalid = showNameError && !name.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setShowNameError(true);

    if (!name.trim()) {
      return;
    }

    try {
      const result = await createLeague({
        variables: {
          input: {
            name: name.trim(),
            isPublic,
            maxPlayers,
            ...(competitionCode ? { competitionCode } : {}),
            ...(showOfficialChannels ? { officialChannelIds: selectedOfficialChannelIds } : {}),
          },
        },
      });

      if (result.data?.createFantasyLeague?.errors.length) {
        setErrors(result.data.createFantasyLeague.errors);
      } else if (result.data?.createFantasyLeague?.fantasyLeague) {
        // Navigate to the newly created league
        const leagueId = result.data.createFantasyLeague.fantasyLeague.id;
        router.push(`/app/ligas/${leagueId}`, 'forward', 'push');
      }
    } catch (error) {
      setErrors(['Ha ocurrido un error al crear la liga']);
      console.error('Error creating league:', error);
    }
  };

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/mis-ligas" />
          </IonButtons>
          <IonTitle className="ml-4">Crear Liga Fantasy</IonTitle>
        </IonToolbar>
      </AppSectionHeader>

      <IonContent className="ion-padding">
        <div className="mx-auto flex w-full max-w-xl flex-col gap-4 py-4">
          <section
            className="rounded-lg border border-blue-200 bg-blue-50 p-4"
            aria-labelledby="create-league-intro-title"
          >
            <div className="flex items-start gap-2">
              <IonIcon
                icon={informationCircleOutline}
                className="mt-0.5 shrink-0 text-blue-600"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <h2
                  id="create-league-intro-title"
                  className="text-sm font-semibold text-slate-900"
                >
                  {CREATE_LEAGUE_INTRO.title}
                </h2>

                {CREATE_LEAGUE_INTRO.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="mt-2 text-xs leading-snug text-slate-600"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 p-4">
                <div className="mb-3">
                  <label
                    htmlFor="fantasy-league-name"
                    className="block text-sm font-semibold text-slate-900"
                  >
                    Nombre de la Liga *
                  </label>

                  <p
                    id="fantasy-league-name-help"
                    className="mt-1 text-xs leading-snug text-slate-500"
                  >
                    {LEAGUE_INFO_CONTENT.name.body}
                  </p>
                </div>

                <IonInput
                  id="fantasy-league-name"
                  className="w-full text-start"
                  fill="outline"
                  value={name}
                  onIonInput={(e) => handleNameInput(e.detail.value)}
                  onIonBlur={() => setShowNameError(true)}
                  placeholder="Ej: Liga de Amigos"
                  aria-invalid={isNameInvalid}
                  aria-required="true"
                  aria-describedby={
                    isNameInvalid
                      ? 'fantasy-league-name-help fantasy-league-name-error'
                      : 'fantasy-league-name-help'
                  }
                />

                {isNameInvalid && (
                  <IonText color="danger" id="fantasy-league-name-error" className="mt-2 block">
                    <p className="flex items-center gap-1.5 text-sm">
                      <IonIcon icon={alertCircleOutline} aria-hidden="true" />
                      {NAME_REQUIRED_ERROR}
                    </p>
                  </IonText>
                )}
              </div>

              {showCompetitionSelector && (
                <div className="border-b border-slate-100 p-4">
                  <span
                    id="fantasy-league-competition-label"
                    className="block text-sm font-semibold text-slate-900"
                  >
                    Competición
                  </span>
                  <p className="mt-1 text-xs leading-snug text-slate-500">
                    Los equipos de la liga se formarán con jugadores de esta competición.
                  </p>

                  <div className="relative mt-3">
                    <SelectField
                      icon={trophyOutline}
                      value={
                        selectedCompetition ? (
                          <CompetitionName
                            displayName={selectedCompetition.displayName}
                            autonomousCommunity={selectedCompetition.autonomousCommunity}
                          />
                        ) : (
                          '—'
                        )
                      }
                      aria-label="Seleccionar competición"
                      onClick={() => competitionSelectRef.current?.open()}
                    />
                    <IonSelect
                      ref={competitionSelectRef}
                      aria-label="Competición"
                      interface="popover"
                      value={competitionCode}
                      onIonChange={(event) => setSelectedCompetitionCode(event.detail.value as string)}
                      className="pointer-events-none absolute inset-0 opacity-0"
                    >
                      {availableCompetitions.map((competition) => (
                        <IonSelectOption key={competition.code} value={competition.code}>
                          {formatCompetitionLabel(competition)}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>
                </div>
              )}

              <IonList
                className="p-0"
                style={{ '--background': '#ffffff', background: '#ffffff' } as React.CSSProperties}
              >
                {/* Pública */}
                <IonItem style={{ '--background': '#ffffff' } as React.CSSProperties}>
                  <div className="flex min-h-16 w-full items-center justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <span className="block text-sm font-medium text-slate-900">
                        Liga Pública
                      </span>

                      <p
                        id="fantasy-league-public-help"
                        className="mt-1 text-xs leading-snug text-slate-500"
                      >
                        {LEAGUE_INFO_CONTENT.public.body}
                      </p>
                    </div>

                    <IonToggle
                      checked={isPublic}
                      onIonChange={(e) => setIsPublic(e.detail.checked)}
                      aria-describedby="fantasy-league-public-help"
                    />
                  </div>
                </IonItem>

                {/* Máx jugadores */}
                <IonItem lines="none" style={{ '--background': '#ffffff' } as React.CSSProperties}>
                  <div className="flex min-h-16 w-full items-center justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <span className="block text-sm font-medium text-slate-900">
                        Máx. Jugadores Mercado
                      </span>

                      <p
                        id="fantasy-league-max-players-help"
                        className="mt-1 text-xs leading-snug text-slate-500"
                      >
                        {LEAGUE_INFO_CONTENT.maxPlayers.body}
                      </p>
                    </div>

                    <IonInput
                      className="shrink-0 text-left"
                      fill="outline"
                      type="number"
                      inputmode="numeric"
                      min={MAX_PLAYERS_MARKET_MIN}
                      max={MAX_PLAYERS_MARKET_MAX}
                      value={maxPlayers}
                      onIonInput={(e) => handleMaxPlayersInput(e.detail.value)}
                      aria-label="Máximo de jugadores en mercado"
                      aria-describedby="fantasy-league-max-players-help"
                      style={{ width: '88px' }}
                    />
                  </div>
                </IonItem>
              </IonList>
            </div>

            {showOfficialChannels && (
              <OfficialChannelsFollowBlock
                action="create"
                channels={officialChannels}
                selectedChannelIds={selectedOfficialChannelIds}
                onSelectedChannelIdsChange={handleOfficialChannelSelectionChange}
              />
            )}

            {errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3" role="alert">
                {errors.map((error, index) => (
                  <IonText color="danger" key={index}>
                    <p className="flex items-center gap-1.5 text-sm">
                      <IonIcon icon={alertCircleOutline} aria-hidden="true" />
                      {error}
                    </p>
                  </IonText>
                ))}
              </div>
            )}

            <IonButton expand="block" type="submit" disabled={loading}>
              {loading ? <IonSpinner name="crescent" /> : 'Crear Liga'}
            </IonButton>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
};
